import { WSActions, WSAlert, WSList } from "@wingspanhq/fe-component-library";
import React, { useMemo, useState } from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";
import {
  queriesPayerEngagements,
  queriesPayerRows,
  queriesPayers,
  queriesWorkItems,
  queriesWorkLogs
} from "@query";
import { selectorPayerEngagementsFromV3 } from "@selectors/selectorPayerEngagementsFromV3";
import { selectorPayerResponseFromV3 } from "@selectors/selectorPayerResponseFromV3";
import { Layout } from "../../../../shared/components/Layout";
import { useBrowserPageTitle } from "../../../../shared/hooks/useBrowserPageTitle";
import { selectorPayerDisplayName } from "../../../../shared/selectors/selectorPayerDisplayName";
import { CardWorkItems } from "../../components/CardWorkItems";
import { filterActiveWorkItems } from "../../utils/filterActiveWorkItems";
import { WorkLogEngagementDetails } from "../../components/WorkLogEngagementDetails";
import { useModalConvert } from "../../modals/ModalConvert";
import { useModalExit } from "./ModalExit";
import { useQueryInvoicingConfigForPayee } from "../../../../query/invoicingConfig/queries/useQueryInvoicingConfigForPayee";
import { selectorIsPayeeAllowedToInitiateInvoice } from "../../../../Invoices/selectors/selectorIsPayeeAllowedToInitiateInvoice";
import { AlertClientNotAllowedSubmission } from "../../../../Invoices/components/AlertClientNotAllowedSubmission";
import { useSession } from "@hooks/useSession";

export const RouteEdit: React.FC<
  RouteComponentProps<{ workLogId: string }>
> = props => {
  const { accountId } = useSession();
  const history = useHistory();
  const workLogId = props.match.params.workLogId;
  const queryWorkLog = queriesWorkLogs.getWorkLog.useQuery({ workLogId });
  const workLog = queryWorkLog.data;

  // v3 read: WorkLog's readonly `payerEngagementId` is the counterpart
  // PayerEngagement id (distinct from its own `payeeEngagementId`) — search
  // rows by it to resolve payerId + the full v1-shape engagement
  // (requirements/eligibility/payeeOwnedData) the dynamic work-item enum
  // lookups (CardWorkItems' `resources.payerPayeeEngagement`) still need —
  // see selectorPayerEngagementsFromV3.
  const queryPayerRow = queriesPayerRows.searchPayerRows.useQuery(
    {
      filter: {
        engagements_payerEngagementId: {
          eq: workLog?.payerEngagementId ?? undefined
        }
      }
    },
    {
      enabled: !!workLog?.payerEngagementId
    }
  );

  const payerId = queryPayerRow.data?.data?.[0]?.id;
  const payerPayeeEngagementId = workLog?.payerEngagementId ?? undefined;

  const queryPayer = queriesPayers.getPayer.useQuery(
    { payerId: payerId! },
    { enabled: !!payerId, retry: false }
  );
  // CRUD PayerEngagement — only needed here for its embedded `workDefinitions`
  // (the search row doesn't carry them).
  const queryPayerEngagement =
    queriesPayerEngagements.getPayerEngagement.useQuery(
      { payerId: payerId!, payerEngagementId: payerPayeeEngagementId! },
      { enabled: !!payerId && !!payerPayeeEngagementId }
    );
  const payer = queryPayer.data;
  const payerPayeeEngagement = selectorPayerEngagementsFromV3(
    queryPayerRow.data?.data?.[0]
  ).find(e => e.payerPayeeEngagementId === payerPayeeEngagementId);

  const workDefinitions = queryPayerEngagement.data?.workDefinitions ?? [];

  const queryInvoicingConfigForPayee = useQueryInvoicingConfigForPayee(
    payerId as string,
    {
      enabled: !!payerId
    }
  );

  const isPayeeNotAllowedToInitiateInvoice =
    !selectorIsPayeeAllowedToInitiateInvoice(
      queryInvoicingConfigForPayee.data,
      accountId
    );

  const browserTitle = useMemo(() => {
    if (!payerPayeeEngagementId) {
      return "Log Work";
    }

    if (!payer) {
      return "Log Work";
    }

    const payerName = selectorPayerDisplayName(payer);

    if (payerPayeeEngagement) {
      return `Log Work – ${payerName} • ${payerPayeeEngagement.engagementName}`;
    }

    return `Log Work – ${payerName}`;
  }, [payer, payerPayeeEngagement, payerPayeeEngagementId]);

  useBrowserPageTitle(browserTitle);

  const queryWorkItems = queriesWorkItems.listWorkItems.useQuery({
    filter: { workLogId: { eq: workLogId } }
  });

  const modalExit = useModalExit();
  const modalConvert = useModalConvert();

  const [createError, setCreateError] = useState(false);

  // TODO(v3): `invoiceId` renamed to `convertedResourceId` on v3 WorkLog.
  const isInvoiceCreated = !!workLog?.convertedResourceId;

  return (
    <Layout
      title="Work log"
      onClose={() => {
        modalExit.open();
      }}
    >
      <WSList gap="2XL">
        <WorkLogEngagementDetails
          shimmer={queryPayerRow.isInitialLoading}
          payer={payer ? selectorPayerResponseFromV3(payer) : undefined}
          payerPayeeEngagement={payerPayeeEngagement}
        />

        {isPayeeNotAllowedToInitiateInvoice && (
          <AlertClientNotAllowedSubmission />
        )}

        <CardWorkItems
          shimmer={
            queryWorkItems.isInitialLoading ||
            queryPayerEngagement.isInitialLoading
          }
          noEdit={isInvoiceCreated}
          workLogId={workLogId}
          payerId={payerId!}
          payerPayeeEngagementId={payerPayeeEngagementId!}
          workItems={queryWorkItems.data?.data || []}
          workDefinitions={workDefinitions}
          resources={{
            payerPayeeEngagement
          }}
        />

        {createError && (
          <WSAlert
            title="Invoice creation error"
            theme="warning"
            icon="alert"
            onDismiss={() => {
              setCreateError(false);
            }}
          >
            There was an error creating the invoice. Please try again. If the
            issue persists, please contact support.
          </WSAlert>
        )}

        <WSActions
          alignment="fill"
          disabled={isPayeeNotAllowedToInitiateInvoice}
          buttons={[
            {
              label: isInvoiceCreated ? "View invoice" : "Create invoice",
              disabled:
                filterActiveWorkItems(queryWorkItems.data?.data || [])
                  .length === 0,
              onClick: () => {
                if (isInvoiceCreated) {
                  history.push(
                    `/member/invoices/${workLog.convertedResourceId}`
                  );
                } else {
                  modalConvert.open({
                    workLogId,
                    onClose: result => {
                      setCreateError(result?.createError || false);
                    }
                  });
                }
              }
            }
          ]}
        />
      </WSList>
    </Layout>
  );
};
